import { Link, useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";
import { isHealthSource } from "@/lib/brand";

export interface FooterProps {
  /** Optional: use when layout has a fixed bottom nav so the footer sits above it. */
  className?: string;
  /** When set, Contact Us opens this callback (e.g. a modal) instead of navigating to /contact. */
  onContactClick?: () => void;
}

/**
 * Site footer matching app-pwa Footer.vue. In page flow below md (so it never covers content on
 * phones), fixed to the bottom from md up. Hidden on /task route.
 */
export function Footer({ className, onContactClick }: FooterProps) {
  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const hideFooter = path === "/task";

  if (hideFooter) return null;

  return (
    <footer
      className={cn(
        "static flex shrink-0 items-center justify-center bg-healthsource-chrome px-3 pt-1 pb-[max(0.25rem,env(safe-area-inset-bottom,0px))] text-white shadow-[0_-1px_3px_rgba(0,0,0,0.08)] md:fixed md:bottom-0 md:left-0 md:right-0 md:z-40 md:px-4",
        className
      )}
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="flex w-full max-w-[1200px] flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <nav className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0" aria-label="Footer navigation">
          {onContactClick ? (
            <button
              type="button"
              onClick={onContactClick}
              className={cn(
                "inline-flex min-h-11 items-center rounded px-2 text-[0.75rem] font-semibold text-white/95 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/90 focus-visible:outline-offset-2"
              )}
              aria-label="Contact us"
            >
              Contact Us
            </button>
          ) : (
            <Link
              to="/contact"
              className={cn(
                "inline-flex min-h-11 items-center rounded px-2 text-[0.75rem] font-semibold text-white/95 no-underline transition-colors hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/90 focus-visible:outline-offset-2"
              )}
              aria-label="Contact us"
            >
              Contact Us
            </Link>
          )}
          <span className="select-none px-0.5 font-semibold text-white/50" aria-hidden>
            |
          </span>
          <span className="inline-flex min-h-11 items-center px-1.5 text-[0.75rem] font-semibold text-white/75">
            Terms
            <span className="ml-1 font-normal text-white/60">(soon)</span>
          </span>
          <span className="select-none px-0.5 font-semibold text-white/50" aria-hidden>
            |
          </span>
          <span className="inline-flex min-h-11 items-center px-1.5 text-[0.75rem] font-semibold text-white/75">
            Privacy
            <span className="ml-1 font-normal text-white/60">(soon)</span>
          </span>
        </nav>
        {/* Vendor credit is HealthSource-only; other practices' patients don't know HealthCORE. */}
        {isHealthSource() && (
          <div className="pb-1 text-center text-[0.6875rem] leading-tight text-white/90 md:pb-0">
            Copyright {new Date().getFullYear()} &copy; HealthCORE Technology, LLC. All rights reserved.
          </div>
        )}
      </div>
    </footer>
  );
}
